import React, { useEffect, useMemo, useRef } from 'react';
import { matchPath, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { getAvailablePages, mergeRoutes, sessionId, SiteConfigManager } from '@atlas/core';
import { COMPONENT_TYPES, staticRoutes } from '@atlas/routing';
import { ContactSupportTeam } from '@atlas/ui';

import StaticPage from 'common/StaticPage/StaticPage';
import SignUp from 'components/SignUp/SignUp';
import useBodyClassName from 'hooks/useBodyClassName';
import { usePostHog } from 'hooks/usePostHog';
import useOauth from 'src/components/MainRouter/useOauth';
import { pushPageViewEvent } from 'src/helpers/customGtmEvents168';
import { useShowMessagesPopup } from 'src/hooks/useShowMessagesPopup';

const {
  All_PROVIDERS,
  BLOG,
  CASINO,
  CASINO_HOME,
  CASINO_LIVE,
  CLIENT_PROFILE,
  LUCKY_DAY,
  PROMOTION_TOURNAMENTS,
  PROMOTIONS,
  RESULTS,
  SMARTICO_MISSIONS,
  SPORT_LIVE,
  SPORT_PREMATCH,
  TOURNAMENTS,
  TV_GAMES,
  VIRTUAL_GAME,
} = COMPONENT_TYPES;

const { signup, staticPage } = staticRoutes;

const availableGeneralRoutes = {
  [All_PROVIDERS]: {},
  [BLOG]: {},
  [CASINO]: {},
  [CASINO_HOME]: {},
  [CASINO_LIVE]: {},
  [CLIENT_PROFILE]: {},
  [LUCKY_DAY]: {},
  [PROMOTIONS]: {},
  [PROMOTION_TOURNAMENTS]: {},
  [RESULTS]: {},
  [SMARTICO_MISSIONS]: {},
  [SPORT_LIVE]: {},
  [SPORT_PREMATCH]: {},
  [TOURNAMENTS]: {},
  [TV_GAMES]: {},
  [VIRTUAL_GAME]: {},
  pages: {},
};

const ShowMessagePopup = () => {
  useShowMessagesPopup();
  return null;
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

const debouncedPushPageViewEvent = debounce(pushPageViewEvent, 800);

const setupTitleObserver = () => {
  const titleEl = document.querySelector('title');
  if (titleEl) {
    const observer = new MutationObserver(() => {
      debouncedPushPageViewEvent();
    });

    observer.observe(titleEl, {
      childList: true,
      subtree: true,
    });
  }

  debouncedPushPageViewEvent();
};

setupTitleObserver();

const MainRouter = ({
  CustomStaticPage = null,
  defaultRedirect,
  isShowMessagesPopup = true,
  withoutSignUpPage = false,
}) => {
  const location = useLocation();
  useBodyClassName();

  const availablePages = useMemo(
    () => getAvailablePages(availableGeneralRoutes, SiteConfigManager.getExtraConfig('components')),
    [],
  );

  const routes = useMemo(() => {
    const pages = [
      ...availablePages,
      {
        component: CustomStaticPage || StaticPage,
        optionalPath: '/:slug',
        path: staticPage,
      },
    ];

    if (!withoutSignUpPage && SiteConfigManager.getConfig('config.config.enable_registration')) {
      pages.push({
        component: SignUp,
        path: signup,
      });
    }

    return pages;
  }, [availablePages, CustomStaticPage, withoutSignUpPage]);

  const mergedRoutes = useMemo(() => mergeRoutes(routes), [routes]);

  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const routeToRedirect = defaultRedirect || routes[0].path || clientProfilePath;

  useOauth();

  const { init, login } = usePostHog();

  useEffect(() => {
    init().then(() => {
      login(sessionId);
    });
  }, [init, login]);

  const staticPageComponent = CustomStaticPage || StaticPage;
  const isInitialRouteViewRef = useRef(true);

  useEffect(() => {
    if (isInitialRouteViewRef.current) {
      isInitialRouteViewRef.current = false;
      return;
    }

    const matchedRoute = mergedRoutes.find(route =>
      matchPath(location.pathname, { path: route.path }),
    );

    if (matchedRoute && matchedRoute.component !== staticPageComponent) {
      debouncedPushPageViewEvent();
    }
  }, [location.pathname, location.search, mergedRoutes, staticPageComponent]);

  if (!availablePages.length) {
    return <ContactSupportTeam />;
  }

  return (
    <>
      {isShowMessagesPopup && <ShowMessagePopup />}
      <Switch>
        {mergedRoutes.map(({ component, path }) => (
          <Route component={component} key={path} path={path} />
        ))}

        <Redirect to={`${routeToRedirect}${location.search}`} />
      </Switch>
    </>
  );
};

MainRouter.propTypes = {
  CustomStaticPage: PropTypes.node,
  defaultRedirect: PropTypes.string,
  isShowMessagesPopup: PropTypes.bool,
  withoutSignUpPage: PropTypes.bool,
};

export default MainRouter;
